import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core'
import { Alert, Autocomplete } from '@material-ui/lab'
import { makeStyles } from '@material-ui/core/styles'
import CloudUploadIcon from '@material-ui/icons/CloudUpload'
import LinkIcon from '@material-ui/icons/Link'
import RefreshIcon from '@material-ui/icons/Refresh'
import ArchiveIcon from '@material-ui/icons/Archive'
import axios from 'axios'
import ReviewQueue from './ReviewQueue'

const STATUS_COLORS = {
  queued: '#9e9e9e',
  processing: '#2196f3',
  completed: '#4caf50',
  completed_with_warnings: '#ff9800',
  failed: '#f44336',
  archived: '#757575'
}

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3)
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3)
  },
  tableContainer: {
    marginTop: theme.spacing(2)
  },
  statusChip: {
    fontWeight: 600,
    color: '#fff',
    textTransform: 'capitalize'
  },
  fileInput: {
    marginTop: theme.spacing(2)
  },
  dialogField: {
    marginBottom: theme.spacing(2)
  }
}))

function formatDate(dateStr) {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleString()
}

function formatStatus(status) {
  if (!status) return ''
  return status.replace(/_/g, ' ')
}

export default function VendorCatalogAdmin() {
  const classes = useStyles()
  const [catalogs, setCatalogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [importOpen, setImportOpen] = useState(false)
  const [importForm, setImportForm] = useState({
    vendorName: '',
    catalogVersion: '',
    catalogName: '',
    sourceUrl: ''
  })
  const [importMode, setImportMode] = useState('file') // 'file' | 'url'
  const [selectedFile, setSelectedFile] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [showQueue, setShowQueue] = useState(false)
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' })
  const [archiveConfirm, setArchiveConfirm] = useState(null)
  const [vendors, setVendors] = useState([])
  const pollingRef = useRef(null)

  useEffect(() => {
    axios.get('/api/company/getVendors')
      .then(res => {
        const active = (res.data || []).filter(v => v.active && v.name)
        setVendors(active)
      })
      .catch(() => {})
  }, [])

  const fetchCatalogs = useCallback(async () => {
    try {
      const res = await axios.get('/api/vendor-catalogs')
      setCatalogs(res.data)
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to load catalogs', severity: 'error' })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchCatalogs()
  }, [fetchCatalogs])

  // Poll while any catalog is queued or processing
  useEffect(() => {
    const hasActiveImport = catalogs.some(
      (c) => c.status === 'queued' || c.status === 'processing'
    )

    if (hasActiveImport) {
      pollingRef.current = setInterval(() => {
        fetchCatalogs()
      }, 3000)
    } else if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current)
        pollingRef.current = null
      }
    }
  }, [catalogs, fetchCatalogs])

  const handleImportOpen = () => {
    setImportForm({ vendorName: '', catalogVersion: '', catalogName: '', sourceUrl: '' })
    setImportMode('file')
    setSelectedFile(null)
    setImportOpen(true)
  }

  const handleImportClose = () => {
    setImportOpen(false)
  }

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0])
    }
  }

  const handleImportSubmit = async () => {
    if (!importForm.vendorName) {
      setSnackbar({ open: true, message: 'Please select a vendor', severity: 'error' })
      return
    }
    if (!importForm.catalogVersion) {
      setSnackbar({ open: true, message: 'Catalog Version is required', severity: 'error' })
      return
    }
    if (importMode === 'file' && !selectedFile) {
      setSnackbar({ open: true, message: 'Please select a PDF file', severity: 'error' })
      return
    }
    if (importMode === 'url' && !importForm.sourceUrl) {
      setSnackbar({ open: true, message: 'Please enter a PDF URL', severity: 'error' })
      return
    }

    setUploading(true)
    try {
      if (importMode === 'url') {
        await axios.post('/api/vendor-catalogs/import-url', {
          vendorName: importForm.vendorName,
          catalogVersion: importForm.catalogVersion,
          catalogName: importForm.catalogName || `${importForm.vendorName} ${importForm.catalogVersion}`,
          sourceUrl: importForm.sourceUrl
        })
      } else {
        const formData = new FormData()
        formData.append('vendorName', importForm.vendorName)
        formData.append('catalogVersion', importForm.catalogVersion)
        formData.append('catalogName', importForm.catalogName || `${importForm.vendorName} ${importForm.catalogVersion}`)
        formData.append('catalogFile', selectedFile)

        await axios.post('/api/vendor-catalogs', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
      }

      setSnackbar({ open: true, message: 'Catalog import started', severity: 'success' })
      setImportOpen(false)
      fetchCatalogs()
    } catch (err) {
      const msg = err.response?.data?.error || 'Import failed'
      setSnackbar({ open: true, message: msg, severity: 'error' })
    } finally {
      setUploading(false)
    }
  }

  const handleReparse = async (catalogId) => {
    try {
      await axios.post(`/api/vendor-catalogs/${catalogId}/reparse`)
      setSnackbar({ open: true, message: 'Reparse started', severity: 'success' })
      fetchCatalogs()
    } catch (err) {
      const msg = err.response?.data?.error || 'Reparse failed'
      setSnackbar({ open: true, message: msg, severity: 'error' })
    }
  }

  const handleArchive = async (catalogId) => {
    try {
      await axios.patch(`/api/vendor-catalogs/${catalogId}/archive`)
      setSnackbar({ open: true, message: 'Catalog archived', severity: 'success' })
      setArchiveConfirm(null)
      fetchCatalogs()
    } catch (err) {
      const msg = err.response?.data?.error || 'Archive failed'
      setSnackbar({ open: true, message: msg, severity: 'error' })
    }
  }

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }))
  }

  if (showQueue) {
    return <ReviewQueue onBack={() => setShowQueue(false)} />
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Typography variant="h5">Vendor Catalog Management</Typography>
        <Box style={{ display: 'flex', gap: 8 }}>
          <Button variant="outlined" onClick={() => setShowQueue(true)}>
            Review Queue
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<CloudUploadIcon />}
            onClick={handleImportOpen}
          >
            Import Catalog
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Catalog Name</TableCell>
              <TableCell>Vendor</TableCell>
              <TableCell>Version</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Products</TableCell>
              <TableCell align="right">Warnings</TableCell>
              <TableCell>Imported</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {catalogs.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No catalogs imported yet
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {catalogs.map((catalog) => {
              const isActive = catalog.status === 'queued' || catalog.status === 'processing'
              return (
                <TableRow key={catalog._id}>
                  <TableCell>{catalog.catalogName}</TableCell>
                  <TableCell>{catalog.vendorName}</TableCell>
                  <TableCell>{catalog.catalogVersion}</TableCell>
                  <TableCell>
                    <Chip
                      label={formatStatus(catalog.status)}
                      size="small"
                      className={classes.statusChip}
                      style={{ backgroundColor: STATUS_COLORS[catalog.status] || '#9e9e9e' }}
                    />
                  </TableCell>
                  <TableCell align="right">{catalog.recordCount || 0}</TableCell>
                  <TableCell align="right">{catalog.warningCount || 0}</TableCell>
                  <TableCell>{formatDate(catalog.importedAt)}</TableCell>
                  <TableCell align="center">
                    {isActive ? (
                      <CircularProgress size={24} />
                    ) : (
                      <>
                        <Tooltip title="Reparse">
                          <IconButton
                            size="small"
                            onClick={() => handleReparse(catalog._id)}
                          >
                            <RefreshIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Archive">
                          <IconButton
                            size="small"
                            onClick={() => setArchiveConfirm(catalog._id)}
                          >
                            <ArchiveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Import Dialog */}
      <Dialog open={importOpen} onClose={handleImportClose} maxWidth="sm" fullWidth>
        <DialogTitle>Import Catalog</DialogTitle>
        <DialogContent>
          <Autocomplete
            className={classes.dialogField}
            options={vendors}
            getOptionLabel={(option) => option.name || ''}
            value={vendors.find(v => v.name === importForm.vendorName) || null}
            onChange={(_, selected) => setImportForm((prev) => ({ ...prev, vendorName: selected ? selected.name : '' }))}
            renderInput={(params) => (
              <TextField {...params} label="Vendor" fullWidth placeholder="Search vendors..." />
            )}
          />
          <TextField
            className={classes.dialogField}
            label="Catalog Version"
            fullWidth
            required
            value={importForm.catalogVersion}
            onChange={(e) => setImportForm((prev) => ({ ...prev, catalogVersion: e.target.value }))}
            placeholder="e.g. 2026-Spring"
          />
          <TextField
            className={classes.dialogField}
            label="Catalog Name (optional)"
            fullWidth
            value={importForm.catalogName}
            onChange={(e) => setImportForm((prev) => ({ ...prev, catalogName: e.target.value }))}
            helperText="Defaults to Vendor + Version if left blank"
          />
          {importMode === 'file' ? (
            <>
              <Box className={classes.fileInput}>
                <Button variant="outlined" component="label">
                  {selectedFile ? selectedFile.name : 'Choose PDF File'}
                  <input
                    type="file"
                    accept="application/pdf"
                    hidden
                    onChange={handleFileChange}
                  />
                </Button>
              </Box>
              <Box mt={1}>
                <Button size="small" color="primary" onClick={() => setImportMode('url')}>
                  Or import from URL
                </Button>
              </Box>
            </>
          ) : (
            <>
              <TextField
                className={classes.dialogField}
                label="PDF URL"
                fullWidth
                value={importForm.sourceUrl}
                onChange={(e) => setImportForm((prev) => ({ ...prev, sourceUrl: e.target.value }))}
                placeholder="https://vendor.com/catalog.pdf"
                style={{ marginTop: 16 }}
              />
              <Box>
                <Button size="small" color="primary" onClick={() => setImportMode('file')}>
                  Or upload a file
                </Button>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleImportClose} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={handleImportSubmit}
            color="primary"
            variant="contained"
            disabled={uploading}
            startIcon={uploading ? <CircularProgress size={18} /> : (importMode === 'url' ? <LinkIcon /> : <CloudUploadIcon />)}
          >
            {uploading ? (importMode === 'url' ? 'Fetching...' : 'Uploading...') : (importMode === 'url' ? 'Fetch & Import' : 'Upload & Import')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Archive Confirmation Dialog */}
      <Dialog open={!!archiveConfirm} onClose={() => setArchiveConfirm(null)}>
        <DialogTitle>Archive Catalog</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to archive this catalog? Archived catalogs will no longer appear
            in search results.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiveConfirm(null)}>Cancel</Button>
          <Button
            onClick={() => handleArchive(archiveConfirm)}
            color="secondary"
            variant="contained"
          >
            Archive
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}
